/**
 * src/lib/programModel.js — mô hình dữ liệu chương trình / tài trợ.
 *
 * Dùng chung cho: trang quản trị /admin/chuong-trinh, trang /chuong-trinh,
 * script kéo dữ liệu lúc build và bước kiểm tra dữ liệu. Hàm thuần, không phụ
 * thuộc React hay Firebase, để chạy được cả trong trình duyệt lẫn Node.
 *
 * Trạng thái một bản ghi trên Firestore:
 *   DRAFT      — đang soạn, chỉ người quản trị thấy.
 *   PUBLISHED  — hiện trên website.
 *   ARCHIVED   — ẩn khỏi website. Dùng để ẩn cả chương trình có sẵn trong code
 *                (src/data/programs.js) mà không phải sửa code.
 */
import { trangThai, TRANG_THAI } from '../../shared/programs.js';

export const LOAI_CHUONG_TRINH = [
  { value: 'CORPORATE_ACCELERATOR', label: 'Chương trình tăng tốc của doanh nghiệp' },
  { value: 'EQUITY_FREE_ACCELERATOR', label: 'Tăng tốc không lấy cổ phần' },
  { value: 'OPEN_INNOVATION', label: 'Đổi mới mở / triển khai thử với doanh nghiệp' },
  { value: 'PRIZE_CHALLENGE', label: 'Cuộc thi có giải thưởng' },
  { value: 'MATCHING_GRANT', label: 'Tài trợ đối ứng' },
  { value: 'RESEARCH_GRANT', label: 'Tài trợ nghiên cứu' },
  { value: 'GOVERNMENT_SUPPORT', label: 'Hỗ trợ của nhà nước' },
  { value: 'BLENDED_IMPACT', label: 'Tài trợ / đầu tư tác động kết hợp' },
  { value: 'CLOUD_CREDITS', label: 'Tín dụng đám mây / hạ tầng' },
  { value: 'OTHER', label: 'Khác' },
];

export const LOAI_HO_TRO = [
  { value: 'GRANT', label: 'Tiền tài trợ không hoàn lại' },
  { value: 'IN_KIND', label: 'Hỗ trợ hiện vật / dịch vụ / cố vấn' },
  { value: 'EQUITY', label: 'Đầu tư đổi cổ phần' },
  { value: 'DEBT', label: 'Vốn vay' },
];

import { chuanHoaDanhMuc } from './fundModel.js';

export const TRANG_THAI_BAN_GHI = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

/** Khoá được phép lưu trên Firestore — khớp với firestore.rules. */
export const KHOA_CHUONG_TRINH = [
  'programId', 'slug', 'name', 'shortName', 'organizer', 'kind', 'website', 'logoUrl',
  'edition', 'benefit', 'eligibility', 'process', 'content', 'alumni', 'provenance',
  'status', 'publish', 'updatedAt', 'updatedBy',
];

const LA_NGAY = /^\d{4}-\d{2}-\d{2}$/;

const chuoi = (v, max = 300) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const ngayHoacNull = (v) => (LA_NGAY.test(String(v || '')) ? String(v) : null);
const danhSach = (v, soMuc = 20, max = 300) =>
  (Array.isArray(v) ? v : String(v || '').split('\n'))
    .map((x) => chuoi(x, max))
    .filter(Boolean)
    .slice(0, soMuc);
const tapMa = (v, soMuc = 30) => [...new Set((Array.isArray(v) ? v : []).map((x) => chuoi(x, 40)).filter(Boolean))].slice(0, soMuc);
const httpsUrl = (v) => {
  const s = chuoi(v, 600);
  return /^https:\/\/\S+$/i.test(s) ? s : '';
};

export function chuanHoaSlug(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** Chuẩn hoá một chương trình đọc từ Firestore, từ form quản trị hoặc từ code. */
export function chuanHoaChuongTrinh(raw = {}) {
  const slug = chuanHoaSlug(raw.slug || raw.programId || raw.name);
  const e = raw.edition || {};
  const status = TRANG_THAI_BAN_GHI.includes(raw.status)
    ? raw.status
    : raw.publish?.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT';
  const out = {
    programId: chuoi(raw.programId, 80) || slug,
    slug,
    name: chuoi(raw.name, 180),
    shortName: chuoi(raw.shortName, 60),
    organizer: chuoi(raw.organizer, 160),
    kind: LOAI_CHUONG_TRINH.some((k) => k.value === raw.kind) ? raw.kind : 'OTHER',
    website: httpsUrl(raw.website),
    logoUrl: httpsUrl(raw.logoUrl),
    edition: e.rolling
      ? { label: chuoi(e.label, 80) || 'Nhận liên tục', rolling: true, opensAt: null, closesAt: null }
      : { label: chuoi(e.label, 80), rolling: false, opensAt: ngayHoacNull(e.opensAt), closesAt: ngayHoacNull(e.closesAt) },
    benefit: {
      capitalTypes: tapMa(raw.benefit?.capitalTypes, 6),
      note: chuoi(raw.benefit?.note, 600),
      // Mức hỗ trợ dạng Money chỉ có ở dữ liệu cũ trong code; giữ nguyên nếu có.
      ...(raw.benefit?.max && typeof raw.benefit.max === 'object' ? { max: raw.benefit.max } : {}),
    },
    eligibility: {
      stages: tapMa(raw.eligibility?.stages),
      geos: tapMa(raw.eligibility?.geos),
      industries: tapMa(raw.eligibility?.industries),
      hardRequirements: danhSach(raw.eligibility?.hardRequirements, 12, 300),
    },
    process: { applyUrl: httpsUrl(raw.process?.applyUrl) },
    content: {
      summary: chuoi(raw.content?.summary, 600),
      tips: danhSach(raw.content?.tips, 8, 400),
    },
    /**
     * Doanh nghiệp đã qua chương trình. Dùng CHUNG bộ chuẩn hoá với danh mục
     * của quỹ (chuanHoaDanhMuc) vì hình dạng dữ liệu y hệt — một bản luật, một
     * chỗ sửa. Khác nhau chỉ ở cách gọi trên giao diện: chương trình không rót
     * vốn theo vòng, nên đây là "đã tham gia" chứ không phải "đã đầu tư".
     */
    ...(chuanHoaDanhMuc(raw.alumni) ? { alumni: chuanHoaDanhMuc(raw.alumni) } : {}),
    provenance: {
      sources: (Array.isArray(raw.provenance?.sources) ? raw.provenance.sources : [])
        .map((s) => ({
          url: httpsUrl(s?.url),
          fetchedAt: ngayHoacNull(s?.fetchedAt),
          note: chuoi(s?.note, 200),
        }))
        .filter((s) => s.url)
        .slice(0, 8),
      lastVerifiedAt: ngayHoacNull(raw.provenance?.lastVerifiedAt),
    },
    status,
    publish: { status: status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT' },
    updatedAt: chuoi(raw.updatedAt, 40),
    updatedBy: chuoi(raw.updatedBy, 200),
  };
  return out;
}

/**
 * Lỗi phải sửa trước khi XUẤT BẢN. Cùng chuẩn với scripts/check-funds.mjs, để
 * người quản trị biết ngay trên form chứ không phải lúc build mới vỡ.
 */
export function kiemTraChuongTrinh(p) {
  const loi = [];
  if (!p.name) loi.push('Thiếu tên chương trình.');
  if (!p.slug) loi.push('Thiếu đường dẫn (slug).');
  if (!p.organizer) loi.push('Thiếu đơn vị tổ chức.');
  if (!p.process?.applyUrl) loi.push('Thiếu đường dẫn nộp hồ sơ / hướng dẫn chính thức (https://…).');
  if (!p.content?.summary) loi.push('Thiếu mô tả ngắn.');
  if (!p.provenance?.sources?.length) loi.push('Cần ít nhất một nguồn chính thức (https://…).');
  if (!p.provenance?.lastVerifiedAt) loi.push('Thiếu ngày kiểm chứng.');
  const al = p.alumni;
  if (al?.items?.length) {
    if (!al.verifiedAt) loi.push('Danh sách doanh nghiệp đã tham gia cần ngày kiểm chứng.');
    if (!al.source) loi.push('Danh sách doanh nghiệp đã tham gia cần đường dẫn nguồn.');
    if (al.items.some((x) => !x.whatTheyDo)) loi.push('Mỗi doanh nghiệp đã tham gia cần một câu mô tả doanh nghiệp đó làm gì.');
    if (al.items.some((x) => !x.industries?.length)) loi.push('Mỗi doanh nghiệp đã tham gia cần ít nhất một ngành.');
  }
  const e = p.edition || {};
  if (!e.rolling && !e.label && !e.opensAt && !e.closesAt) {
    loi.push('Đợt hiện tại cần có tên đợt hoặc ít nhất một ngày mở / đóng.');
  }
  if (e.opensAt && e.closesAt && e.closesAt < e.opensAt) loi.push('Ngày đóng đang trước ngày mở.');
  return loi;
}

/**
 * Gộp danh sách có sẵn trong code với bản ghi trên Firestore.
 * Firestore thắng khi trùng programId: PUBLISHED thay thế, ARCHIVED ẩn đi.
 * DRAFT không ảnh hưởng tới website.
 */
export function gopChuongTrinh(goc = [], remote = []) {
  const theoId = new Map(goc.map((p) => [p.programId, p]));
  for (const r of remote) {
    if (!r?.programId) continue;
    if (r.status === 'ARCHIVED') theoId.delete(r.programId);
    else if (r.status === 'PUBLISHED') theoId.set(r.programId, r);
  }
  return [...theoId.values()];
}

/**
 * Nhắc việc cho người quản trị: chương trình nào cần tra lại lịch.
 * Trả về chuỗi lý do, hoặc '' nếu không cần.
 */
export function canXemLai(p, homNay, soNgayCu = 180) {
  const tt = trangThai(p.edition, homNay);
  if (tt === TRANG_THAI.DA_DONG) return 'Đợt đã đóng, tra lịch kỳ mới';
  if (tt === TRANG_THAI.CHUA_RO) return 'Chưa có lịch, tra lại trang chính thức';
  const v = p.provenance?.lastVerifiedAt;
  if (v && LA_NGAY.test(v)) {
    const d = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
    if ((d(homNay) - d(v)) / 86400000 > soNgayCu) return `Kiểm chứng đã hơn ${Math.round(soNgayCu / 30)} tháng`;
  }
  return '';
}
